import React, { useEffect, useState } from "react";
import SEO from "../../components/SEO";
import { HiPhoto } from "react-icons/hi2";
import AuthService from "../../services/auth.service";
import UserService from "../../services/user.service";
import ParcelOrderService from "../../services/parcel-order.service";
import { initializeApp } from 'firebase/app';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const firebaseConfig = {
	apiKey: "AIzaSyCA8lStBJcswiLF8KIaN2iPhx-TUJ6YS7U",
	authDomain: "liftsearch-31ac1.firebaseapp.com",
	databaseURL: "https://<DATABASE_NAME>.firebaseio.com",
	storageBucket: "gs://liftsearch-31ac1.appspot.com/"
};

initializeApp(firebaseConfig);

const ProfileTab = ({ user, setUser }) => {
	const [form, setForm] = useState({
		first_name: user?.first_name || '',
		last_name: user?.last_name || '',
		email: user?.email || '',
		cellphone: user?.cellphone || '',
		gender: user?.gender || 'not_selected',
	});
	const [file, setFile] = useState(null);
	const [saving, setSaving] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [stats, setStats] = useState({ total: 0, pending: 0, delivered: 0, spent: 0 });

	useEffect(() => {
		ParcelOrderService.index().then((data) => {
			const upcoming = data?.upcoming_parcels || [];
			const past = data?.past_parcels || [];
			const all = [...upcoming, ...past];
			const paid = all.filter(o => o.status === 'paid' || o.status === 1);
			const pending = all.filter(o => o.delivery_status !== 'delivered' && o.delivery_status !== 5);
			const delivered = all.filter(o => o.delivery_status === 'delivered' || o.delivery_status === 5);
			const spent = paid.reduce((sum, o) => sum + ((o.price?.value || 0) * (o.quantity || 1)), 0);
			setStats({ total: all.length, pending: pending.length, delivered: delivered.length, spent });
		}).catch(() => {});
	}, []);

	const handleSave = async () => {
		setSaving(true);
		try {
			await AuthService.update(form.cellphone, form.first_name, form.last_name, form.email, null);
			const stored = JSON.parse(localStorage.getItem('user'));
			if (stored?.user) {
				stored.user = { ...stored.user, ...form };
				localStorage.setItem('user', JSON.stringify(stored));
				setUser(stored.user);
			}
			alert('Profile updated!');
		} catch {
			alert('Failed to update profile');
		}
		setSaving(false);
	};

	const handleUpload = async () => {
		if (!file) return;
		setUploading(true);
		try {
			const storage = getStorage();
			const storageRef = ref(storage, `uploads/${file.name}`);
			await uploadBytes(storageRef, file);
			const downloadUrl = await getDownloadURL(storageRef);
			await UserService.update(user.id, { profile_pic_url: downloadUrl });
			const stored = JSON.parse(localStorage.getItem('user'));
			if (stored?.user) {
				stored.user.profile_pic_url = downloadUrl;
				localStorage.setItem('user', JSON.stringify(stored));
				setUser(stored.user);
			}
			setFile(null);
		} catch {
			alert('Failed to upload photo');
		}
		setUploading(false);
	};

	return (
		<div className="flex flex-col gap-4 sm:gap-6">
			{/* Stats */}
			<div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
				{[
					{ label: 'Total Orders', value: stats.total, color: 'text-purple-700' },
					{ label: 'Pending', value: stats.pending, color: 'text-amber-600' },
					{ label: 'Delivered', value: stats.delivered, color: 'text-green-600' },
					{ label: 'Total Spent', value: `R${stats.spent}`, color: 'text-blue-600' },
				].map((s, i) => (
					<div key={i} className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100 text-center shadow-sm">
						<p className={`text-xl sm:text-2xl font-bold ${s.color}`}>{s.value}</p>
						<p className="text-[10px] sm:text-xs font-medium text-gray-500 mt-1">{s.label}</p>
					</div>
				))}
			</div>

			{/* Photo Upload */}
			<div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
				<h2 className="text-base sm:text-lg font-bold text-gray-800 mb-4">Profile Photo</h2>
				<div className="flex items-center gap-4">
					<div className="relative group shrink-0">
						<img
							className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-purple-200"
							src={user?.profile_pic_url || '//ssl.gstatic.com/accounts/ui/avatar_2x.png'}
							alt="Profile"
						/>
						<label className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
							<HiPhoto className="w-5 h-5 text-white" />
							<input type="file" className="hidden" accept="image/*" onChange={(e) => setFile(e.target.files[0])} />
						</label>
					</div>
					{file ? (
						<div className="flex-1 bg-purple-50 rounded-xl p-3 border border-purple-200">
							<p className="text-xs text-purple-700 font-medium truncate">{file.name}</p>
							<button onClick={handleUpload} disabled={uploading}
								className="mt-2 bg-purple-600 text-white px-4 py-1.5 rounded-lg text-xs font-semibold hover:bg-purple-700 transition-colors">
								{uploading ? 'Uploading...' : 'Upload'}
							</button>
						</div>
					) : (
						<label className="text-sm text-purple-600 font-semibold cursor-pointer hover:text-purple-700">
							Change Photo
							<input type="file" className="hidden" accept="image/*" onChange={(e) => setFile(e.target.files[0])} />
						</label>
					)}
				</div>
			</div>

			{/* Details Form */}
			<div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
				<h2 className="text-base sm:text-lg font-bold text-gray-800 mb-4">Personal Details</h2>
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
					{[
						{ label: 'First Name', key: 'first_name', type: 'text' },
						{ label: 'Last Name', key: 'last_name', type: 'text' },
						{ label: 'Email', key: 'email', type: 'email' },
						{ label: 'Phone', key: 'cellphone', type: 'text' },
					].map((f) => (
						<div key={f.key} className="flex flex-col">
							<label className="text-purple-700 font-semibold text-xs sm:text-sm mb-1">{f.label}</label>
							<input
								type={f.type}
								value={form[f.key]}
								onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
								className="bg-gray-50 border-2 border-gray-200 hover:border-purple-300 focus:border-purple-500 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-sm focus:outline-none transition-colors"
							/>
						</div>
					))}
					<div className="flex flex-col">
						<label className="text-purple-700 font-semibold text-xs sm:text-sm mb-1">Gender</label>
						<select
							value={form.gender}
							onChange={(e) => setForm({ ...form, gender: e.target.value })}
							className="bg-gray-50 border-2 border-gray-200 hover:border-purple-300 focus:border-purple-500 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-sm focus:outline-none transition-colors"
						>
							<option value="not_selected">Select</option>
							<option value="male">Male</option>
							<option value="female">Female</option>
						</select>
					</div>
				</div>
				<div className="flex justify-end mt-4 sm:mt-6">
					<button onClick={handleSave} disabled={saving}
						className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl text-sm font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md disabled:opacity-50">
						{saving ? 'Saving...' : 'Save Changes'}
					</button>
				</div>
			</div>
		</div>
	);
};

const RewardsTab = ({ user }) => {
	const [referralData, setReferralData] = useState(null);
	const [copied, setCopied] = useState(false);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!user?.id) return;
		UserService.getReferrals(user.id).then((res) => {
			setReferralData(res.data);
		}).catch(() => {}).finally(() => setLoading(false));
	}, [user?.id]);

	const copyLink = () => {
		const code = referralData?.referral_code || user?.referral_code;
		if (!code) return;
		navigator.clipboard.writeText(`https://parcelin.co.za/ref/${code}`);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	const referralCode = referralData?.referral_code || user?.referral_code;

	return (
		<div className="flex flex-col gap-4 sm:gap-6">
			{/* Wallet & Stats */}
			<div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
				<div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 sm:p-5 text-white shadow-md col-span-2 sm:col-span-1">
					<p className="text-xs text-white/70">Wallet Balance</p>
					<p className="text-2xl sm:text-3xl font-bold mt-1">R{Number(referralData?.wallet_balance || user?.wallet_balance || 0).toFixed(2)}</p>
				</div>
				<div className="bg-white rounded-xl p-4 border border-gray-100 text-center shadow-sm">
					<p className="text-2xl font-bold text-purple-700">{referralData?.total_referred || 0}</p>
					<p className="text-[10px] sm:text-xs font-medium text-gray-500 mt-1">People Referred</p>
				</div>
				<div className="bg-white rounded-xl p-4 border border-gray-100 text-center shadow-sm">
					<p className="text-2xl font-bold text-green-600">
						{referralData?.referred_users?.filter(u => u.has_paid_order).length || 0}
					</p>
					<p className="text-[10px] sm:text-xs font-medium text-gray-500 mt-1">Earned Rewards</p>
				</div>
			</div>

			{/* Referral Link */}
			<div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
				<div className="flex items-center justify-between mb-3">
					<h2 className="text-base sm:text-lg font-bold text-gray-800">Your Referral Link</h2>
					<span className="bg-green-100 text-green-700 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-full">
						R50 per referral
					</span>
				</div>
				<p className="text-xs sm:text-sm text-gray-500 mb-4">
					Share this link with friends. When they place their first paid order, you earn R50 in wallet credit.
				</p>
				{referralCode ? (
					<div className="flex items-center gap-2">
						<input
							type="text" readOnly
							value={`https://parcelin.co.za/ref/${referralCode}`}
							className="flex-1 bg-purple-50 border-2 border-purple-200 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm text-purple-700 font-medium focus:outline-none min-w-0"
						/>
						<button onClick={copyLink}
							className={`px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
								copied ? 'bg-green-500 text-white' : 'bg-purple-600 text-white hover:bg-purple-700'
							}`}>
							{copied ? 'Copied!' : 'Copy'}
						</button>
					</div>
				) : (
					<p className="text-sm text-gray-400">Log out and log back in to see your referral link.</p>
				)}
			</div>

			{/* Referred Users */}
			<div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
				<h2 className="text-base sm:text-lg font-bold text-gray-800 mb-4">People You Referred</h2>
				{loading ? (
					<p className="text-sm text-gray-400 text-center py-4">Loading...</p>
				) : referralData?.referred_users?.length > 0 ? (
					<div className="space-y-3">
						{referralData.referred_users.map((u) => (
							<div key={u.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
								<div className="flex items-center gap-3 min-w-0">
									<div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
										<span className="text-purple-700 font-bold text-xs sm:text-sm">
											{u.first_name?.[0]}{u.last_name?.[0]}
										</span>
									</div>
									<div className="min-w-0">
										<p className="text-sm font-semibold text-gray-800 truncate">{u.first_name} {u.last_name}</p>
										<p className="text-[10px] sm:text-xs text-gray-400">
											Joined {new Date(u.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
										</p>
									</div>
								</div>
								{u.has_paid_order ? (
									<span className="bg-green-100 text-green-700 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-full whitespace-nowrap">
										+R50 earned
									</span>
								) : (
									<span className="bg-gray-100 text-gray-500 text-[10px] sm:text-xs font-medium px-2 sm:px-3 py-1 rounded-full whitespace-nowrap">
										Pending order
									</span>
								)}
							</div>
						))}
					</div>
				) : (
					<div className="text-center py-6">
						<p className="text-gray-400 text-sm">No referrals yet</p>
						<p className="text-gray-300 text-xs mt-1">Share your link to start earning</p>
					</div>
				)}
			</div>
		</div>
	);
};

export function ProfilePage() {
	const [user, setUser] = useState(null);
	const [tab, setTab] = useState('profile');

	useEffect(() => {
		const data = AuthService.getCurrentUser();
		if (data?.user) setUser(data.user);
	}, []);

	if (!user) return null;

	return (
		<div className="w-full min-h-screen bg-gray-50 py-6 sm:py-8">
			<SEO title="My Profile" noIndex />
			<div className="max-w-2xl mx-auto px-4 flex flex-col gap-4 sm:gap-6">

				{/* Header */}
				<div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-4 sm:p-6 text-white shadow-lg">
					<div className="flex items-center gap-4">
						<img
							className="w-14 h-14 sm:w-20 sm:h-20 rounded-full object-cover border-3 border-white/30 shadow-md shrink-0"
							src={user.profile_pic_url || '//ssl.gstatic.com/accounts/ui/avatar_2x.png'}
							alt="Profile"
						/>
						<div className="min-w-0 flex-1">
							<h1 className="text-lg sm:text-2xl font-bold truncate">{user.first_name} {user.last_name}</h1>
							<p className="text-white/70 text-xs sm:text-sm truncate">{user.email}</p>
							<p className="text-white/70 text-xs sm:text-sm">{user.cellphone}</p>
						</div>
					</div>
				</div>

				{/* Tabs */}
				<div className="flex bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
					{['profile', 'rewards'].map((t) => (
						<button
							key={t}
							onClick={() => setTab(t)}
							className={`flex-1 py-3 text-sm font-semibold transition-colors ${
								tab === t
									? 'bg-purple-600 text-white'
									: 'text-gray-500 hover:text-purple-600 hover:bg-purple-50'
							}`}
						>
							{t === 'profile' ? 'Profile' : 'Rewards'}
						</button>
					))}
				</div>

				{/* Tab Content */}
				{tab === 'profile' ? (
					<ProfileTab user={user} setUser={setUser} />
				) : (
					<RewardsTab user={user} />
				)}

			</div>
		</div>
	);
}
